import { useMutation } from '@tanstack/react-query'
import { Check, ChevronDown, Clock } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SettingsSection } from '~/components/common/SettingsSection'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { changeSessionTimeOut } from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'

const timeOptions: Record<string, number> = {
  '5 min': 5,
  '20 min': 20,
  '30 min': 30,
  '1 hour': 60,
  '2 hours': 120,
  '6 hours': 360,
  '12 hours': 720,
  '1 day': 1440,
  '3 days': 4320,
  '7 days': 10080,
  '30 days': 43200
}

interface SessionTimeoutSectionProps {
  sessionTimeoutTime: string
  setSessionTimeoutTime: (value: string) => void
}

export function SessionTimeoutSection({
  sessionTimeoutTime,
  setSessionTimeoutTime
}: SessionTimeoutSectionProps) {
  const { refetch, currentWorkspace } = useWorkspaceContext()
  const isAdmin = currentWorkspace?.is_admin ?? false
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  const { mutate: updateSessionTimeOut } = useMutation({
    mutationFn: ({ uuid, timeout }: { uuid: string; timeout: number }) =>
      changeSessionTimeOut(uuid, timeout),
    onSuccess: () => {
      refetch()
      BetterToast.success('Session timeout updated')
    },
    onError: () => {
      BetterToast.error('Update failed')
    }
  })

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY - 32,
        left: rect.right + window.scrollX - 223,
        width: 223
      })
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY - 32,
          left: rect.right + window.scrollX - 223,
          width: 223
        })
      }
    }
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (key: string) => {
    if (!currentWorkspace?.uuid) return
    setSessionTimeoutTime(key)
    setIsOpen(false)

    const minutes = timeOptions[key]
    if (minutes !== undefined) {
      updateSessionTimeOut({ uuid: currentWorkspace.uuid, timeout: minutes })
    }
  }

  const formatDisplayValue = (key: string): string => {
    const minutes = timeOptions[key]
    if (minutes === undefined) return key
    if (minutes >= 1440) return `${minutes / 1440} Days`
    if (minutes >= 60) return `${minutes / 60} Hours`
    return `${minutes} Min`
  }

  return (
    <SettingsSection>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
            <Clock className='w-[18px] h-[18px]' />
          </span>
          <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
            Session Timeout
          </span>
        </div>

        <button
          ref={triggerRef}
          type='button'
          onClick={() => isAdmin && setIsOpen(!isOpen)}
          className={cm(
            'flex items-center gap-2 w-[240px] h-12 py-2 px-3 rounded-xl justify-end cursor-pointer hover:opacity-80 transition-opacity',
            !isAdmin && 'cursor-default opacity-70'
          )}
          disabled={!isAdmin}
        >
          <span className='font-mono font-semibold text-[15px] leading-normal text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis whitespace-nowrap select-none'>
            {formatDisplayValue(sessionTimeoutTime)}
          </span>
          <ChevronDown className='w-[18px] h-[18px] text-tokens-neutral-icon-solid' />
        </button>

        {isOpen &&
          typeof window !== 'undefined' &&
          createPortal(
            <div
              ref={dropdownRef}
              className='absolute bg-white rounded-[16px] shadow-[0px_6px_16px_rgba(0,0,0,0.08),0px_3px_6px_rgba(0,0,0,0.12),0px_9px_28px_rgba(0,0,0,0.05)] z-[9999] p-2 flex flex-col gap-2 max-h-[300px] overflow-y-auto'
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                visibility: dropdownPosition.width > 0 ? 'visible' : 'hidden'
              }}
              onMouseDown={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              {Object.keys(timeOptions).map(key => {
                const isSelected = key === sessionTimeoutTime
                return (
                  <div
                    key={key}
                    className={cm(
                      'h-8 shrink-0 w-full rounded-lg gap-2 truncate px-3 flex items-center justify-between',
                      'text-[14px] font-medium font-sans select-none text-[rgba(0,0,0,0.88)]',
                      'hover:bg-[rgba(0,0,0,0.04)] cursor-pointer transition-colors',
                      isSelected && 'text-[#65558F]'
                    )}
                    onMouseDown={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSelect(key)
                    }}
                  >
                    <span className='truncate min-w-0'>{key}</span>
                    {isSelected && <Check className='w-4 h-4 shrink-0 text-[#65558F]' />}
                  </div>
                )
              })}
            </div>,
            document.body
          )}
      </div>
    </SettingsSection>
  )
}
